/**
 * Period-based date calculation utilities.
 * End dates are inclusive (start day counts as day 1 of the period).
 * Months use day clamping to prevent overflow (e.g., Jan 31 + 1 month → Feb 27).
 * Used by both the Create Hold Request form and tests.
 */

function parseDate(dateStr: string): Date | null {
  if (!dateStr) return null;

  let d: Date;
  if (dateStr.includes('/')) {
    const parts = dateStr.split('/');
    if (parts.length < 3) return null;
    const month = parseInt(parts[0]!, 10) - 1;
    const day = parseInt(parts[1]!, 10);
    let year = parseInt(parts[2]!, 10);
    if (year < 100) year += 2000;
    d = new Date(year, month, day);
    // Reject invalid calendar dates (e.g., 2/31 normalizes to March)
    if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) {
      return null;
    }
  } else {
    // YYYY-MM-DD format
    const parts = dateStr.split('-');
    if (parts.length < 3) return null;
    const year = parseInt(parts[0]!, 10);
    const month = parseInt(parts[1]!, 10) - 1;
    const day = parseInt(parts[2]!, 10);
    d = new Date(year, month, day);
    // Reject invalid calendar dates (e.g., 2026-02-31 normalizes to March)
    if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) {
      return null;
    }
  }

  return isNaN(d.getTime()) ? null : d;
}

function formatDate(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

/** Parse a string as a strict positive integer. Returns null for non-integers (e.g., "1.5", "2abc"). */
function parsePositiveInt(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const n = Number(value.trim());
  return n > 0 ? n : null;
}

/**
 * Calculate end date from start date + number of periods + type of period.
 * For days/weeks/4wks: start + (N × unit) - 1
 * For months: start + N months - 1 day (with day clamping)
 */
export function calculateEndDate(startDateStr: string, noOfPeriodsStr: string, typeOfPeriod: string): string {
  if (!startDateStr || !noOfPeriodsStr || !typeOfPeriod) return '';

  const noOfPeriods = parsePositiveInt(noOfPeriodsStr);
  if (noOfPeriods === null) return '';

  const startDate = parseDate(startDateStr);
  if (!startDate) return '';

  const endDate = new Date(startDate);

  switch (typeOfPeriod.toLowerCase()) {
    case 'days':
      endDate.setDate(endDate.getDate() + noOfPeriods - 1);
      break;
    case 'hours':
      break;
    case 'weeks':
      endDate.setDate(endDate.getDate() + (noOfPeriods * 7) - 1);
      break;
    case '4wks':
      endDate.setDate(endDate.getDate() + (noOfPeriods * 28) - 1);
      break;
    case 'months': {
      const targetMonth = startDate.getMonth() + noOfPeriods;
      const targetYear = startDate.getFullYear() + Math.floor(targetMonth / 12);
      const targetMon = targetMonth % 12;
      const lastDayOfTargetMonth = new Date(targetYear, targetMon + 1, 0).getDate();
      const clampedDay = Math.min(startDate.getDate(), lastDayOfTargetMonth);
      endDate.setFullYear(targetYear, targetMon, clampedDay - 1);
      break;
    }
    default:
      return '';
  }

  return formatDate(endDate);
}

/**
 * Calculate start date from end date + number of periods + type of period (reverse).
 * For days/weeks/4wks: end - (N × unit) + 1
 * For months: (end + 1 day) - N months (with day clamping)
 */
export function calculateStartDate(endDateStr: string, noOfPeriodsStr: string, typeOfPeriod: string): string {
  if (!endDateStr || !noOfPeriodsStr || !typeOfPeriod) return '';

  const noOfPeriods = parsePositiveInt(noOfPeriodsStr);
  if (noOfPeriods === null) return '';

  const endDate = parseDate(endDateStr);
  if (!endDate) return '';

  const startDate = new Date(endDate);

  switch (typeOfPeriod.toLowerCase()) {
    case 'days':
      startDate.setDate(startDate.getDate() - noOfPeriods + 1);
      break;
    case 'hours':
      break;
    case 'weeks':
      startDate.setDate(startDate.getDate() - (noOfPeriods * 7) + 1);
      break;
    case '4wks':
      startDate.setDate(startDate.getDate() - (noOfPeriods * 28) + 1);
      break;
    case 'months': {
      // NOTE: Month-end clamping in the forward calculation is lossy.
      // e.g., forward: 1/31 → clamp to Feb 28 → -1 = 2/27
      // reverse: 2/27 → +1 = 2/28 → -1 month = 1/28 (not the original 1/31)
      // The reverse returns the start date that reproduces the end date when
      // run through forward calculation again (round-trip stable, not original-preserving).
      const dayAfterEnd = new Date(endDate);
      dayAfterEnd.setDate(dayAfterEnd.getDate() + 1);
      const targetMonth = dayAfterEnd.getMonth() - noOfPeriods;
      const targetYear = dayAfterEnd.getFullYear() + Math.floor(targetMonth / 12);
      const targetMon = ((targetMonth % 12) + 12) % 12;
      const lastDayOfTargetMonth = new Date(targetYear, targetMon + 1, 0).getDate();
      const clampedDay = Math.min(dayAfterEnd.getDate(), lastDayOfTargetMonth);
      startDate.setFullYear(targetYear, targetMon, clampedDay);
      break;
    }
    default:
      return '';
  }

  return formatDate(startDate);
}
